import { createContext, useContext, useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { AuthHelpers, useAuthHelper } from '@salesforce/commerce-sdk-react';
import { useQueryClient } from '@tanstack/react-query';
import Radar from 'radar-sdk-js';
import type { ShopperBasketsTypes, ShopperLoginTypes } from 'commerce-sdk-isomorphic';
import { PaymentMethodId } from '../bff-api/baskets/model';
import { useBasket } from '../bff-api/baskets/useBasket';
import { bffGet, bffPatch } from '../bff-api/bff';
import { useAddWishListItem } from '../bff-api/customers/useAddWishListItem';
import { useCustomerGet } from '../bff-api/customers/useCustomerGet';
import { BffQueryKey } from '../bff-api/model/BffQueryKey';
import { StorageKey } from '../hooks/model/StorageKey';
import { COUNTRY_CODE } from '../utils/address-utils';
import { useCustomPreferences, useLogout } from '../utils/api';
import { CustomPreference } from '../utils/api/CustomPreference';
import { isServer } from '../utils/utils';
import { AuthType } from './model/AuthType';
import { isGuest, isRegistered } from './utils/customer-utils';
import type { UserLogin } from '../pages/checkout/model/UserLogin';
import type { CustomerContext } from './model/CustomerContext';

export const Context = createContext<CustomerContext>({} as CustomerContext);

export const CustomerProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const queryClient = useQueryClient();
    const addToWishlist = useAddWishListItem();
    const { data: customer, isLoading: isCustomerLoading } = useCustomerGet();
    const logout = useLogout();
    const loginMutation = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C);
    const logoutMutation = useAuthHelper(AuthHelpers.Logout);
    const basket = useBasket();
    const preferences =
        useCustomPreferences([
            CustomPreference.RADAR_ENABLED,
            CustomPreference.RADAR_PUBLISHABLE,
        ]) ?? {};
    const radarEnabled = preferences[CustomPreference.RADAR_ENABLED];
    const radarPublishable = preferences[CustomPreference.RADAR_PUBLISHABLE];

    useEffect(() => {
        if (radarEnabled && radarPublishable) {
            Radar.initialize(radarPublishable as string);
        }
    }, [radarEnabled, radarPublishable]);

    useEffect(() => {
        if (radarEnabled && radarPublishable) {
            Radar.setUserId(
                (customer?.authType === AuthType.REGISTERED
                    ? customer?.customerNo
                    : customer?.customerId) ?? '',
            );
            Radar.setDescription(customer?.login ?? AuthType.GUEST);
        }
    }, [radarEnabled, radarPublishable, customer]);

    const ctx = useMemo<CustomerContext>(
        (): CustomerContext =>
            ({
                get isLoading() {
                    return ctx.isRegistered ? isCustomerLoading : !customer?.customerId;
                },

                get customer() {
                    return ctx.isRegistered
                        ? customer
                        : {
                              customerId: customer?.customerId,
                          };
                },

                get addresses() {
                    return customer?.addresses ?? [];
                },

                get defaultAddress() {
                    return customer?.addresses?.[0];
                },

                get hasAddresses() {
                    return (customer?.addresses ?? []).length > 0;
                },

                get isInitialized() {
                    return !!customer?.customerId;
                },

                get isRegistered() {
                    return isRegistered(customer);
                },

                get isGuest() {
                    return isGuest(customer);
                },

                get savedCreditCards() {
                    return (
                        customer?.paymentInstruments?.filter(
                            (paymentInstrument) =>
                                paymentInstrument.paymentMethodId ===
                                PaymentMethodId.CREDIT_CARD.valueOf(),
                        ) ?? []
                    );
                },

                async login(credentials?: UserLogin | null) {
                    if (!isServer) await window.sfccSessionCreationPromise;
                    if (credentials) {
                        const prevBasket = { ...basket.data };
                        const sameDayDeliveryAddress = prevBasket?.shipments
                            ? prevBasket.shipments.find(
                                  (shipment) => shipment.c_shipmentType === 'same-day-delivery',
                              )?.shippingAddress
                            : null;
                        let loginData: ShopperLoginTypes.TokenResponse;
                        try {
                            loginData = await loginMutation.mutateAsync({
                                username: credentials.email,
                                password: credentials.password,
                            });
                        } catch (ex) {
                            if (ex instanceof Error) {
                                const { response } = ex as { response?: { status?: number } };
                                if (typeof response?.status === 'number') {
                                    if (response.status === 401) {
                                        let loginErrorCheck: {
                                            locked?: boolean;
                                        } = {
                                            locked: false,
                                        };
                                        try {
                                            loginErrorCheck = await bffGet<{
                                                locked?: boolean;
                                            }>('/customers/login-errors', {
                                                httpParams: [{ email: credentials.email }],
                                            });
                                        } catch (e) {
                                            throw new Error('invalid credentials');
                                        }

                                        if (loginErrorCheck.locked) {
                                            throw new Error('account locked');
                                        }

                                        throw new Error('invalid credentials');
                                    }
                                    if (response.status === 400) {
                                        throw new Error('Bad or missing client_id.');
                                    }
                                    if (response.status === 409) {
                                        throw new Error(
                                            'The same loginId and tenantId tried to login twice within 1 second.',
                                        );
                                    }
                                }
                            }
                            console.warn(
                                "The server has encountered a situation that it doesn't know how to handle.",
                                ex,
                            );
                            throw new Error(
                                "The server has encountered a situation that it doesn't know how to handle.",
                            );
                        }

                        // Basket merge call is needed because we don't have any SFCC hook for this yet
                        if (loginData && prevBasket) {
                            try {
                                await bffPatch<ShopperBasketsTypes.Basket>(
                                    '/customers/basket/merge',
                                    {
                                        accessToken: loginData.access_token,
                                        customerId: loginData.customer_id,
                                        body: {
                                            productItems: prevBasket.productItems,
                                            ...Object.fromEntries(
                                                Object.entries(prevBasket).filter((entry) =>
                                                    entry[0].startsWith('c_'),
                                                ),
                                            ),
                                            ...(sameDayDeliveryAddress
                                                ? {
                                                      c_sddAddress: {
                                                          address1: sameDayDeliveryAddress.address1,
                                                          address2: sameDayDeliveryAddress.address2,
                                                          city: sameDayDeliveryAddress.city,
                                                          postalCode:
                                                              sameDayDeliveryAddress.postalCode,
                                                          stateCode:
                                                              sameDayDeliveryAddress.stateCode,
                                                          countryCode:
                                                              sameDayDeliveryAddress.countryCode ??
                                                              COUNTRY_CODE,
                                                      },
                                                  }
                                                : {}),
                                        },
                                    },
                                );
                            } catch (ex) {
                                console.warn('Error merging the basket.', ex);
                            }
                        }
                        const productIdToAdd = sessionStorage.getItem(
                            StorageKey.ADD_PRODUCT_ID_TO_WISHLIST,
                        );
                        if (productIdToAdd) {
                            await addToWishlist.mutateAsync(productIdToAdd);
                            sessionStorage.removeItem(StorageKey.ADD_PRODUCT_ID_TO_WISHLIST);
                        }
                    }
                    await Promise.all([
                        queryClient.invalidateQueries([BffQueryKey.CUSTOMER]),
                        queryClient.invalidateQueries([BffQueryKey.BASKET]),
                    ]);
                },

                logout: {
                    isLoading: logoutMutation.isLoading || logout.isLoading,
                    mutateAsync: async (): Promise<void> => {
                        if (!isServer) {
                            await window.sfccSessionCreationPromise;
                        }

                        await logoutMutation.mutateAsync();
                        await logout.mutateAsync(null);
                    },
                },
            }) as CustomerContext,

        [customer, basket, isCustomerLoading],
    );

    return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const useCustomer = (): CustomerContext => useContext(Context);
